// Text summary + PDF export for the mortgage scenario calculator.
// The text summary mirrors the source calculator exactly so the WhatsApp /
// email share messages match. The PDF uses pdf-lib via a canvas-rendered
// A4-sized image for each page — same approach as the source.

import { PDFDocument } from "pdf-lib";
import { cash, tax, ni, payment, sdlt, FEE_ITEMS } from "./calc";

const todayUK = () => new Date().toLocaleDateString("en-GB");

const feeAmount = (f, loan, state) =>
  f.percent
    ? (loan * (Number(state.fees[f.key]?.amount) || 0)) / 100
    : Number(state.fees[f.key]?.amount) || 0;

const applicantLines = (state, joint) =>
  (joint ? [1, 2] : [1]).map((n) => {
    const a = state[`applicant${n}`];
    return `Applicant ${n}: ${[a.first, a.last].filter(Boolean).join(" ").trim()}`;
  });

export const buildScenarioSummary = (state, results, buyerLabel, joint) => {
  const v = results.v;
  const selected = FEE_ITEMS.filter((f) => state.fees[f.key]?.on);
  const lines = [
    "ELIN'S CONSULTANCY — Mortgage illustration",
    todayUK(),
    `Application: ${state.applicationType}`,
    `Income basis: ${
      state.incomeMode === "auto"
        ? "Illustrative required income (automatic)"
        : "Entered actual income"
    }`,
    ...applicantLines(state, joint),
    "",
    `Property: ${cash(v.price)} | Deposit: ${v.deposit}% / ${cash((v.price * v.deposit) / 100)}`,
    `Mortgage: ${cash(results.loan)} | LTV: ${(100 - v.deposit).toFixed(1)}%`,
    `Rate: ${v.rate}% (illustrative) | Term: ${v.term} years`,
    `Required annual gross income at ${v.multiple}x: ${cash(results.loan / Math.max(v.multiple, 0.0001))}`,
    `Annual salaries A1 / A2: ${cash(v.income1)} / ${cash(v.income2)}`,
    `Monthly tax A1 / A2: ${cash(results.t1)} / ${cash(results.t2)}`,
    `Monthly NI A1 / A2: ${cash(results.n1)} / ${cash(results.n2)}`,
    `Other monthly deductions: ${cash(v.deductions)} | Net income: ${cash(results.net)}`,
    `Repayment / month: ${cash(results.rep)} | Interest-only / month: ${cash(results.io)}`,
    `Living costs: ${cash(v.costs)} | Remaining after repayment: ${cash(results.net - results.rep - v.costs)}`,
    `Interest-only capital due: ${cash(results.loan)}`,
    "",
    `Buyer: ${buyerLabel}${state.nonresident ? " + nonresident surcharge" : ""}`,
    `SDLT (England/NI): ${cash(results.duty)}`,
    ...selected.map(
      (f) =>
        `${f.label}: ${cash(feeAmount(f, results.loan, state))}${
          f.percent ? ` (${state.fees[f.key].amount}% of mortgage)` : ""
        }`
    ),
    `Upfront costs (selected fees): ${cash(results.feeTotal)}`,
    `Total cost (upfront cash): ${cash(results.cashTotal)} (deposit + SDLT + selected fees)`,
    "Unselected fees excluded / client to arrange.",
    "",
    "Illustration, not approval or an offer. Interest-only needs a repayment strategy. PAYE: 2026/27 England/Wales/NI. Fees paid upfront. Check buyer eligibility and tax with conveyancer.",
  ];
  return lines.join("\n");
};

// Build the sections that both the PDF and the printable preview share.
const buildSections = (state, results, buyerLabel, joint) => {
  const v = results.v;
  const amount = (f) => feeAmount(f, results.loan, state);
  const s = [];
  s.push({
    title: "Application",
    rows: [
      ["Application type", joint ? "Joint applicants" : "Single applicant"],
      ["Created", todayUK()],
      [
        "Income basis",
        state.incomeMode === "auto"
          ? "Calculated required income illustration"
          : "Entered actual salaries",
      ],
    ],
  });
  for (const i of joint ? [1, 2] : [1]) {
    const a = state[`applicant${i}`];
    const val = (k) => a[k]?.toString().trim() || "Not supplied";
    s.push({
      title: `Applicant ${i} — Personal details`,
      rows: [
        ["First name", val("first")],
        ["Surname", val("last")],
        ["Date of birth", a.dob ? a.dob.split("-").reverse().join("-") : "Not supplied"],
        [
          "Housing status",
          val("housing") + (a.housing === "Other" ? ` — ${val("housingOther")}` : ""),
        ],
      ],
    });
    s.push({
      title: `Applicant ${i} — Current address`,
      rows: [
        ["Building / street", val("address")],
        ["Town / city", val("city")],
        ["Postcode", val("postcode")],
        ["Country", val("country")],
        [
          "Living here since",
          a.since ? a.since.split("-").reverse().join("-") : "Not supplied",
        ],
      ],
    });
    (a.previous || []).forEach((p, idx) =>
      s.push({
        title: `Applicant ${i} — Previous address ${idx + 1}`,
        rows: [
          ["Full address", p.address || "Not supplied"],
          ["From", p.from ? p.from.split("-").reverse().join("-") : "Not supplied"],
          ["To", p.to ? p.to.split("-").reverse().join("-") : "Not supplied"],
        ],
      })
    );
  }
  s.push({
    title: "Property & mortgage",
    rows: [
      ["Property price", cash(v.price)],
      ["Deposit", `${v.deposit}% — ${cash((v.price * v.deposit) / 100)}`],
      ["Mortgage required", cash(results.loan)],
      ["Loan-to-value", `${(100 - v.deposit).toFixed(1)}%`],
      ["Interest rate (illustrative)", `${v.rate}%`],
      ["Term", `${v.term} years`],
      ["Income multiple", `${v.multiple}×`],
      ["Required gross income / year", cash(results.loan / Math.max(v.multiple, 0.0001))],
    ],
  });
  s.push({
    title: "Income & PAYE estimate",
    rows: [
      ["Applicant 1 gross / year", cash(v.income1)],
      ...(joint ? [["Applicant 2 gross / year", cash(v.income2)]] : []),
      ["Applicant 1 monthly Income Tax", cash(results.t1)],
      ["Applicant 1 monthly employee NI", cash(results.n1)],
      ...(joint
        ? [
            ["Applicant 2 monthly Income Tax", cash(results.t2)],
            ["Applicant 2 monthly employee NI", cash(results.n2)],
          ]
        : []),
      ["Other monthly net-pay deductions", cash(v.deductions)],
      ["Estimated net income / month", cash(results.net)],
    ],
  });
  s.push({
    title: "Monthly mortgage & budget",
    rows: [
      ["Repayment mortgage / month", cash(results.rep)],
      ["Interest-only / month", cash(results.io)],
      ["Living costs & commitments", cash(v.costs)],
      ["Remaining after repayment & living costs", cash(results.net - results.rep - v.costs)],
      ["Remaining after interest-only & living costs", cash(results.net - results.io - v.costs)],
      ["Interest-only capital due at term-end", cash(results.loan)],
    ],
  });
  s.push({
    title: "Stamp Duty & optional fees",
    rows: [
      ["Buyer situation", buyerLabel],
      ["Nonresident SDLT surcharge", state.nonresident ? "2% selected" : "Not selected"],
      ["Stamp Duty — England / NI", cash(results.duty)],
      ...FEE_ITEMS.map((f) => [
        f.label,
        state.fees[f.key]?.on
          ? `${cash(amount(f))}${f.percent ? ` — ${state.fees[f.key].amount}% of mortgage` : ""}`
          : "Client to arrange / excluded",
      ]),
    ],
  });
  s.push({
    title: "Upfront cost summary",
    highlight: true,
    rows: [
      ["Deposit", cash((v.price * v.deposit) / 100)],
      ["Stamp Duty", cash(results.duty)],
      ["Upfront Costs — selected fees", cash(results.feeTotal)],
      ["Total Cost — upfront cash", cash(results.cashTotal)],
    ],
  });
  s.push({
    title: "Assumptions & notes",
    rows: [
      [
        "Mortgage illustration",
        "Not a mortgage offer or lender approval. Required income is illustrative, not verified earnings. Rate is constant across the selected full term; fees are paid upfront. Interest-only requires a lender-approved capital repayment strategy.",
      ],
      [
        "PAYE estimate",
        "2026/27 England, Wales and Northern Ireland. Standard PAYE salary and employee NI with equal monthly pay. Pension relief and student loans are not calculated automatically.",
      ],
      [
        "Purchase costs",
        "SDLT covers England / Northern Ireland, ordinary individual residential purchases. Verify reliefs, surcharges, replacement conditions and buyer eligibility with a conveyancer.",
      ],
    ],
  });
  s.push({
    title: "Declaration & signature",
    signatures: (joint ? [1, 2] : [1]).map((i) => {
      const a = state[`applicant${i}`];
      const nm = [a.first, a.last].filter(Boolean).join(" ") || `Applicant ${i}`;
      return {
        name: `${joint ? `Applicant ${i} — ` : "Client — "}${nm}`,
        img: a.signature || null,
      };
    }),
  });
  return s;
};

const pdfName = (state) => {
  const name =
    [state.applicant1.first, state.applicant1.last].filter(Boolean).join(" ") ||
    "Client Scenario";
  const d = new Date();
  const dateStr = `${String(d.getDate()).padStart(2, "0")}-${String(d.getMonth() + 1).padStart(2, "0")}-${d.getFullYear()}`;
  return (
    name
      .replace(/[\/:*?"<>|]/g, "")
      .replace(/\s+/g, " ")
      .slice(0, 120) + ` ${dateStr}.pdf`
  );
};

// Render each PDF page to a 1190x1684 canvas (A4 at ~144dpi), then embed as PNG
// into a 595x842pt page in pdf-lib. This mirrors the source approach.
export const exportScenarioPdf = async (state, results, buyerLabel, joint) => {
  const sections = buildSections(state, results, buyerLabel, joint);
  const doc = await PDFDocument.create();
  doc.setTitle(pdfName(state).replace(/\.pdf$/, ""));
  doc.setCreationDate(new Date());

  const canvases = [];
  let canvas, ctx, y;
  const title = pdfName(state).replace(/\.pdf$/, "");
  const today = todayUK();

  const wrap = (text, max, font = "24px Arial") => {
    ctx.font = font;
    const words = String(text).split(/\s+/);
    const lines = [];
    let line = "";
    for (const w of words) {
      const next = line ? line + " " + w : w;
      if (ctx.measureText(next).width > max && line) {
        lines.push(line);
        line = w;
      } else {
        line = next;
      }
    }
    lines.push(line);
    return lines;
  };

  const newPage = () => {
    canvas = document.createElement("canvas");
    canvas.width = 1190;
    canvas.height = 1684;
    ctx = canvas.getContext("2d");
    ctx.fillStyle = "#fff";
    ctx.fillRect(0, 0, 1190, 1684);
    ctx.fillStyle = "#0F3226"; // forest
    ctx.fillRect(0, 0, 1190, 170);
    ctx.fillStyle = "#fff";
    ctx.font = "bold 36px Arial";
    ctx.fillText("ELIN'S CONSULTANCY", 70, 70);
    ctx.font = "24px Arial";
    ctx.fillText("Client mortgage illustration", 70, 115);
    ctx.font = "20px Arial";
    ctx.fillText(title, 70, 147);
    ctx.fillStyle = "#526970";
    ctx.font = "20px Arial";
    ctx.fillText(
      `Elin's Consultancy | Page ${canvases.length + 1} | Created ${today}`,
      70,
      1630
    );
    canvases.push(canvas);
    y = 215;
  };
  newPage();

  for (const section of sections) {
    if (y + 160 > 1570) newPage();
    ctx.fillStyle = section.highlight ? "#0F3226" : "#B99A4A"; // forest or gold
    ctx.fillRect(70, y, 1050, 52);
    ctx.fillStyle = "#fff";
    ctx.font = "bold 25px Arial";
    ctx.fillText(section.title, 90, y + 35);
    y += 72;

    if (section.signatures) {
      for (const sg of section.signatures) {
        const h = 230;
        if (y + h > 1560) newPage();
        ctx.fillStyle = "#F7F2E7"; // cream-3
        ctx.fillRect(70, y, 1050, h);
        ctx.strokeStyle = "#d3cfbf";
        ctx.strokeRect(70, y, 1050, h);
        ctx.fillStyle = "#526970";
        ctx.font = "bold 20px Arial";
        ctx.fillText(sg.name, 90, y + 36);
        if (sg.img) {
          try {
            const img = new Image();
            await new Promise((res, rej) => {
              img.onload = res;
              img.onerror = rej;
              img.src = sg.img;
            });
            const maxW = 900,
              maxH = 120,
              sc = Math.min(maxW / img.width, maxH / img.height, 1);
            ctx.drawImage(img, 90, y + 52, img.width * sc, img.height * sc);
          } catch (e) {
            /* skip */
          }
        }
        ctx.strokeStyle = "#0F3226";
        ctx.beginPath();
        ctx.moveTo(90, y + 185);
        ctx.lineTo(690, y + 185);
        ctx.stroke();
        ctx.fillStyle = "#526970";
        ctx.font = "20px Arial";
        ctx.fillText("Signature", 90, y + 212);
        ctx.fillText("Date: ____________________", 760, y + 205);
        y += h + 18;
      }
      y += 18;
      continue;
    }

    for (let i = 0; i < section.rows.length; i += 2) {
      const pair = section.rows.slice(i, i + 2);
      const width = 510,
        padding = 18;
      const values = pair.map(([, v]) => wrap(v, width - padding * 2));
      const labels = pair.map(([l]) => wrap(l, width - padding * 2, "bold 20px Arial"));
      const lh = Math.max(...labels.map((x) => x.length)) * 25;
      const vh = Math.max(...values.map((x) => x.length)) * 31;
      const h = lh + vh + 48;
      if (y + h > 1570) {
        newPage();
        ctx.fillStyle = "#B99A4A";
        ctx.fillRect(70, y, 1050, 52);
        ctx.fillStyle = "#fff";
        ctx.font = "bold 25px Arial";
        ctx.fillText(section.title + " (continued)", 90, y + 35);
        y += 72;
      }
      pair.forEach(([, _], j) => {
        const x = 70 + j * 540;
        ctx.fillStyle = section.highlight ? "#E8EFEA" : "#F7F2E7";
        ctx.fillRect(x, y, width, h);
        ctx.strokeStyle = "#d3cfbf";
        ctx.strokeRect(x, y, width, h);
        ctx.fillStyle = "#526970";
        ctx.font = "bold 20px Arial";
        labels[j].forEach((l, k) => ctx.fillText(l, x + padding, y + 30 + k * 25));
        ctx.fillStyle = "#0F3226";
        ctx.font = section.highlight ? "bold 26px Arial" : "24px Arial";
        values[j].forEach((l, k) => ctx.fillText(l, x + padding, y + lh + 43 + k * 31));
      });
      y += h + 18;
    }
    y += 18;
  }

  for (const c of canvases) {
    const png = await doc.embedPng(c.toDataURL("image/png"));
    doc.addPage([595, 842]).drawImage(png, { x: 0, y: 0, width: 595, height: 842 });
  }
  const bytes = await doc.save();
  const blob = new Blob([bytes], { type: "application/pdf" });
  const url = URL.createObjectURL(blob);
  const name = pdfName(state);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
  return name;
};

// Native print-friendly preview using the browser's print dialog. Opens a new
// window with a clean A4-styled version of the sections.
export const printScenario = (state, results, buyerLabel, joint) => {
  const sections = buildSections(state, results, buyerLabel, joint);
  const title = pdfName(state).replace(/\.pdf$/, "");
  const esc = (v) =>
    String(v).replace(/[&<>"']/g, (c) =>
      ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c])
    );
  const w = window.open("", "_blank");
  if (!w) return;
  const html = `<!doctype html><html><head><meta charset="utf-8"><title>${esc(title)}</title>
<style>
  * { box-sizing: border-box; }
  body { font: 13px/1.5 Arial, sans-serif; color: #0F3226; margin: 32px; }
  .brand { background: #0F3226; color: #F7F2E7; padding: 18px 24px; }
  .brand h1 { margin: 0 0 2px; font: bold 20px Arial; letter-spacing: 0.1em; }
  .brand p { margin: 0; font-size: 13px; opacity: 0.8; }
  section { margin-top: 20px; page-break-inside: avoid; }
  section > h2 { background: #B99A4A; color: #fff; padding: 10px 16px; margin: 0; font: bold 15px Arial; }
  .rows { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; margin-top: 8px; }
  .row { background: #F7F2E7; padding: 10px 14px; border: 1px solid #d3cfbf; }
  .row b { display: block; font-size: 11px; letter-spacing: 0.1em; text-transform: uppercase; color: #526970; margin-bottom: 2px; }
  .row span { font-size: 14px; color: #0F3226; }
  .highlight .row { background: #E8EFEA; }
  .highlight .row span { font-weight: bold; font-size: 15px; }
  .sig { background: #F7F2E7; padding: 14px; border: 1px solid #d3cfbf; margin-top: 10px; }
  .sig img { max-height: 110px; display: block; margin-top: 6px; }
  .sig .line { border-top: 1px solid #0F3226; margin-top: 20px; }
  .sig .meta { font-size: 11px; color: #526970; margin-top: 8px; }
  @media print { body { margin: 16mm 14mm; } .noprint { display: none; } }
  .noprint { text-align: right; margin-bottom: 12px; }
  .noprint button { font: bold 11px/1 Arial; letter-spacing: 0.14em; text-transform: uppercase; padding: 10px 16px; background: #0F3226; color: #F7F2E7; border: 0; cursor: pointer; }
</style></head>
<body>
<div class="noprint"><button onclick="window.print()">Print / Save PDF</button></div>
<div class="brand"><h1>ELIN'S CONSULTANCY</h1><p>Client mortgage illustration — ${esc(title)}</p></div>
${sections
  .map((s) =>
    s.signatures
      ? `<section><h2>${esc(s.title)}</h2>${s.signatures
          .map(
            (sg) =>
              `<div class="sig"><b>${esc(sg.name)}</b>${
                sg.img ? `<img src="${sg.img}">` : '<div class="line"></div>'
              }<div class="meta">Signature · Date: ____________________</div></div>`
          )
          .join("")}</section>`
      : `<section class="${s.highlight ? "highlight" : ""}"><h2>${esc(s.title)}</h2><div class="rows">${s.rows
          .map(([l, v]) => `<div class="row"><b>${esc(l)}</b><span>${esc(v)}</span></div>`)
          .join("")}</div></section>`
  )
  .join("")}
</body></html>`;
  w.document.open();
  w.document.write(html);
  w.document.close();
};
